import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Modal } from "react-bootstrap";
import "../../assets/css/project-chart.css";

export default function TeamList(props) {
  useTranslation();
  const auth = useSelector((state) => state.auth);

  // ── Core list state ───────────────────────────────────────────────────────
  const [teamList, setTeamList]     = useState([]);
  const [mode, setMode]             = useState(1);
  const [sortAsc, setSortAsc]       = useState(true);
  const [user_id, setUserId]        = useState(0);
  const [clickedId, setClickedId]   = useState(null);
  const dropdownRef                 = useRef(null);

  // ── Invited tab state ────────────────────────────────────────────────────
  const [inviteClickedEmail, setInviteClickedEmail] = useState(null);
  const inviteDropdownRef                           = useRef(null);
  const [resendingEmail, setResendingEmail]         = useState(null);

  // ── Toast state ───────────────────────────────────────────────────────────
  const [toast, setToast] = useState(null); // { message, type: 'success'|'error' }
  const toastTimerRef     = useRef(null);

  const showToast = (message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  };

  // ── Confirmation modal state ──────────────────────────────────────────────
  const [confirmOpen, setConfirmOpen]     = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'deactivate'|'remove', item }
  const [confirmLoading, setConfirmLoading] = useState(false);

  // ── Workspace defaults state ──────────────────────────────────────────────
  const [workspaceDefaults, setWorkspaceDefaults]       = useState({});
  const [defaultsEditOpen, setDefaultsEditOpen]         = useState(false);
  const [defaultBillRate, setDefaultBillRate]           = useState("");
  const [defaultCurrId, setDefaultCurrId]               = useState("");
  const [currencies, setCurrencies]                     = useState([]);

  // ── Edit modal state ──────────────────────────────────────────────────────
  const [editOpen, setEditOpen]                 = useState(false);
  const [team_id, setTeamId]                   = useState(0);
  const [editUserId, setEditUserId]             = useState(null);
  const [memberName, setMemberName]             = useState("");
  const [memberEmail, setMemberEmail]           = useState("");
  const [editMemberType, setEditMemberType]     = useState("internal");
  const [editBillableRate, setEditBillableRate] = useState(0);
  const [editLaborCost, setEditLaborCost]       = useState(0);
  const [editCurrencyId, setEditCurrencyId]     = useState("");
  const [editWorkspaceRole, setEditWorkspaceRole] = useState("MEMBER");
  const [errors, setErrors]                     = useState({});

  // ─────────────────────────────────────────────────────────────────────────
  // Bootstrap
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Decode user_id from base64
    if (auth.user_id !== null && auth.user_id !== undefined) {
      setUserId(atob(auth.user_id));
    }

    // Close dropdown on outside click
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setClickedId(null);
      }
      if (inviteDropdownRef.current && !inviteDropdownRef.current.contains(e.target)) {
        setInviteClickedEmail(null);
      }
    };
    document.addEventListener("click", handleOutside, true);

    // Set mode
    const modeN = props.mode ?? 1;
    setMode(modeN);
    setTeamList([]);
    fetchCurrencies();
    getTeamList(modeN);
    getWorkspaceInfo();

    return () => document.removeEventListener("click", handleOutside, true);
  }, [props.mode, props.reload]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // API helpers
  // ─────────────────────────────────────────────────────────────────────────
  const authHeaders = () => ({
    headers: {
      Authorization: `Bearer ${auth.token}`,
      "Content-Type": "application/json",
    },
  });

  const fetchCurrencies = async () => {
    try {
      const res = await axios.get("/api/team/currencies", authHeaders());
      if (res.status === 200) setCurrencies(res.data.currencies ?? []);
    } catch (err) {
      console.error("fetchCurrencies:", err);
    }
  };

  const getWorkspaceInfo = async () => {
    try {
      const workspace_id = auth.workspace_id;
      const body = JSON.stringify({ workspace_id });
      const res = await axios.post(
        "/api/workspace/listbyworkspaceid",
        body,
        authHeaders()
      );
      // workspace defaults are returned from team list endpoint — nothing extra needed here
    } catch (err) {
      console.error("getWorkspaceInfo:", err);
    }
  };

  const getTeamList = async (modenumber) => {
    try {
      const modeN = modenumber ?? mode;
      let is_active;
      if (modeN == 1)      is_active = 1;
      else if (modeN == 2) is_active = 0;
      else if (modeN == 3) is_active = 3;

      const body = JSON.stringify({
        workspace_id: auth.workspace_id,
        is_active,
      });
      const res = await axios.post("/api/team/list", body, authHeaders());
      if (res.status === 200) {
        setTeamList(res.data.results ?? []);
        if (res.data.workspace_defaults) {
          const wd = res.data.workspace_defaults;
          setWorkspaceDefaults(wd);
          setDefaultBillRate(wd.default_billable_rate ?? "");
          setDefaultCurrId(wd.default_currency_id ?? "");
        }
      }
    } catch (err) {
      console.error("getTeamList:", err);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Sorting
  // ─────────────────────────────────────────────────────────────────────────
  const sortTeamArray = () => {
    const sorted = [...teamList].sort((a, b) => {
      const na = (a.name || "").toLowerCase();
      const nb = (b.name || "").toLowerCase();
      return na < nb ? -1 : na > nb ? 1 : 0;
    });
    if (sortAsc) {
      setSortAsc(false);
      setTeamList(sorted.reverse());
    } else {
      setSortAsc(true);
      setTeamList(sorted);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Deactivate / Activate / Remove
  // ─────────────────────────────────────────────────────────────────────────
  const deactivateUser = async (item) => {
    try {
      const body = JSON.stringify({ workspace_id: auth.workspace_id, user_id: item.user_id });
      const res = await axios.put("/api/team/deactivate", body, authHeaders());
      if (res.status === 200) getTeamList();
    } catch (err) {
      console.error("deactivateUser:", err);
    }
  };

  const activateUser = async (item) => {
    try {
      const body = JSON.stringify({ workspace_id: auth.workspace_id, user_id: item.user_id });
      const res = await axios.put("/api/team/activate", body, authHeaders());
      if (res.status === 200) getTeamList();
    } catch (err) {
      console.error("activateUser:", err);
    }
  };

  const removeUser = async (item) => {
    try {
      const res = await axios.delete("/api/team/remove", {
        ...authHeaders(),
        data: { workspace_id: auth.workspace_id, user_id: item.user_id },
      });
      if (res.status === 200) getTeamList();
    } catch (err) {
      console.error("removeUser:", err);
    }
  };

  const openConfirm = (type, item) => {
    setConfirmAction({ type, item });
    setConfirmOpen(true);
    setClickedId(null);
  };

  const runConfirm = async () => {
    if (!confirmAction) return;
    setConfirmLoading(true);
    const { type, item } = confirmAction;
    if (type === 'deactivate') await deactivateUser(item);
    else if (type === 'activate') await activateUser(item);
    else if (type === 'remove') await removeUser(item);
    setConfirmLoading(false);
    setConfirmOpen(false);
    setConfirmAction(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Edit modal
  // ─────────────────────────────────────────────────────────────────────────
  const openEditModal = (item) => {
    setTeamId(item.team_id);
    setEditUserId(item.user_id);
    setMemberName(item.name ?? `${item.first_name} ${item.last_name}`);
    setMemberEmail(item.email);
    setEditMemberType(item.member_type ?? "internal");
    setEditBillableRate(item.billable_rate ?? 0);
    setEditLaborCost(item.labor_cost ?? 0);
    setEditCurrencyId(item.currency_id ?? "");
    setEditWorkspaceRole(item.workspace_role ?? "MEMBER");
    setErrors({});
    setEditOpen(true);
    setClickedId(null);
  };

  const handleClose = () => setEditOpen(false);

  const validateEditForm = () => {
    const errs = {};
    if (!memberName.trim()) errs.member_name = "Name is required";
    if (editMemberType === "external") {
      if (!editBillableRate && editBillableRate !== 0)
        errs.billable_rate = "Billable rate is required";
      if (!editCurrencyId)
        errs.currency_id = "Currency is required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const editMember = async () => {
    if (!validateEditForm() || team_id === 0) return;
    try {
      const isSelf = String(editUserId) === String(user_id);
      const body = JSON.stringify({
        team_id,
        workspace_id   : auth.workspace_id,
        name           : memberName,
        member_type    : editMemberType,
        billable_rate  : editBillableRate,
        labor_cost     : editLaborCost,
        currency_id    : editCurrencyId || null,
        ...(isSelf ? {} : { workspace_role: editWorkspaceRole }),
      });
      const res = await axios.put("/api/team/edit", body, authHeaders());
      if (res.status === 200) {
        handleClose();
        getTeamList();
      }
    } catch (err) {
      console.error("editMember:", err);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Workspace defaults save
  // ─────────────────────────────────────────────────────────────────────────
  const saveWorkspaceDefaults = async () => {
    try {
      const body = JSON.stringify({
        workspace_id        : auth.workspace_id,
        default_billable_rate: defaultBillRate,
        default_currency_id  : defaultCurrId || null,
      });
      const res = await axios.put("/api/team/workspace-defaults", body, authHeaders());
      if (res.status === 200) {
        setDefaultsEditOpen(false);
        getTeamList();
      }
    } catch (err) {
      console.error("saveWorkspaceDefaults:", err);
    }
  };

  const resendWorkspaceInvite = async (item) => {
    setResendingEmail(item.email);
    setInviteClickedEmail(null);
    try {
      const body = JSON.stringify({ workspace_id: auth.workspace_id, invitation_id: item.invitation_id });
      const res  = await axios.post("/api/team/resend-invite", body, authHeaders());
      if (res.status === 200 && res.data.msg === 'success') {
        showToast(`Invitation resent to ${item.email}`, "success");
      } else {
        showToast(`Could not resend invite: ${res.data.msg || "unknown error"}`, "danger");
      }
    } catch (err) {
      showToast("Failed to resend invite. Please try again.", "danger");
    } finally {
      setResendingEmail(null);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────────────────────
  const getRoleName = (role_id) => {
    if (role_id == 1) return tr("Admin");
    if (role_id == 2) return "Manager";
    return tr("Member");
  };

  const formatRate = (currency, rate) => {
    if (!rate && rate !== 0) return null;
    return currency ? `${currency} ${rate}/hr` : `${rate}/hr`;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  const isAdmin = auth.is_workspace_admin === 1 || auth.workspace_role === 'ADMIN';
  const wd      = workspaceDefaults;

  return (
    <>
      {toast ? (
        <div className="ti-toast-container">
          <div className={`ti-toast ti-toast--${toast.type === "error" ? "danger" : toast.type}`}>
            <span className="ti-toast-msg">{toast.message}</span>
          </div>
        </div>
      ) : null}

      {/* ── Workspace defaults card (admin only, active members tab) ── */}
      {isAdmin && mode == 1 && (
        <div className="tm-defaults-card">
          {!defaultsEditOpen ? (
            <>
              <span className="tm-defaults-card-label">{tr("Workspace defaults for internal members:")} </span>
              <span className="tm-defaults-card-value">
                {wd.default_currency || "—"}{" "}
                {wd.default_billable_rate != null ? `${wd.default_billable_rate}/hr` : "—"}
              </span>
              <button
                className="tm-defaults-edit-btn"
                onClick={() => setDefaultsEditOpen(true)}
              >{tr("Edit")} </button>
            </>
          ) : (
            <div className="tm-defaults-edit">
              <span className="tm-defaults-card-label" style={{ flexShrink: 0 }}>{tr("Default rate:")} </span>
              <input
                type="number"
                placeholder={tr("Rate (e.g. 75)")}
                value={defaultBillRate}
                onChange={(e) => setDefaultBillRate(e.target.value)}
                style={{ width: 110 }}
              />
              <select
                value={defaultCurrId}
                onChange={(e) => setDefaultCurrId(e.target.value)}
                style={{ width: 100 }}
              >
                <option value="">{tr("Currency")}</option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.currency}
                  </option>
                ))}
              </select>
              <button className="tm-defaults-edit-save" onClick={saveWorkspaceDefaults}>{tr("Save")} </button>
              <button
                className="tm-defaults-edit-cancel"
                onClick={() => {
                  setDefaultsEditOpen(false);
                  setDefaultBillRate(wd.default_billable_rate ?? "");
                  setDefaultCurrId(wd.default_currency_id ?? "");
                }}
              >{tr("Cancel")} </button>
            </div>
          )}
        </div>
      )}

      <div className="tm-table-wrap">
        {/* ── Header row ── */}
        {mode != 3 && (
          <div className="tm-row--header">
            <div className="tm-cell tm-cell--avatar" />
            <div className="tm-cell tm-cell--name">
              <span className="tm-header-label">
                Name
                <span className="tm-sort-arrows" onClick={sortTeamArray}>
                  <i className="fa fa-angle-up" />
                  <i className="fa fa-angle-down" />
                </span>
              </span>
            </div>
            <div className="tm-cell tm-cell--role">
              <span className="tm-header-label">{tr("Role")}</span>
            </div>
            <div className="tm-cell tm-cell--type">
              <span className="tm-header-label">{tr("Type")}</span>
            </div>
            <div className="tm-cell tm-cell--email">
              <span className="tm-header-label">{tr("Email")}</span>
            </div>
            <div className="tm-cell tm-cell--rate">
              <span className="tm-header-label">{tr("Billable Rate")}</span>
            </div>
            <div className="tm-cell tm-cell--cost">
              <span className="tm-header-label">{tr("Labor Cost")}</span>
            </div>
            {isAdmin && (
              <div className="tm-cell tm-cell--actions" />
            )}
          </div>
        )}

        {/* ── Invited list ── */}
        {mode == 3 &&
          teamList.map((item, idx) => (
            <div className="tm-row--data" key={idx}>
              <div className="tm-cell tm-cell--avatar">
                <div
                  className="tm-avatar"
                  style={{ background: "#d1d5db", color: "#6b7280" }}
                >
                  <svg width="14" height="14" viewBox="0 0 18 18" fill="none">
                    <g stroke="#9ca3af" strokeWidth="2" strokeLinecap="round">
                      <path d="M8 3.5V8m0 0l2.7 1.8" />
                      <circle cx="8" cy="8" r="8" />
                    </g>
                  </svg>
                </div>
              </div>
              <div className="tm-cell tm-cell--name">
                <div className="tm-member-name">{item.name}</div>
                <div className="tm-member-role" style={{ color: "#f59e0b", fontStyle: "italic" }}>{tr("Invitation pending")} </div>
              </div>
              <div className="tm-cell tm-cell--role" />
              <div className="tm-cell tm-cell--type" />
              <div className="tm-cell tm-cell--email">
                <span className="tm-email">{item.email}</span>
              </div>
              <div className="tm-cell tm-cell--rate" />
              <div className="tm-cell tm-cell--cost" />
              {isAdmin && (
                <div className="tm-cell tm-cell--actions" ref={inviteClickedEmail === item.email ? inviteDropdownRef : null}>
                  <div className="tm-dropdown">
                    <div
                      className={`tm-dot-btn${inviteClickedEmail === item.email ? " active" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setInviteClickedEmail(inviteClickedEmail === item.email ? null : item.email);
                      }}
                      title={tr("More actions")}
                    >
                      <svg width="4" height="16" viewBox="0 0 4 16">
                        <path fill="currentColor" fillRule="evenodd" d="M0 2a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm0 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm0-6a2 2 0 1 1 4 0 2 2 0 0 1-4 0z"/>
                      </svg>
                    </div>
                    {inviteClickedEmail === item.email && (
                      <div className="tm-dropdown-menu">
                        <div
                          className="tm-dropdown-item"
                          onClick={() => resendWorkspaceInvite(item)}
                          style={{ opacity: resendingEmail === item.email ? 0.5 : 1, pointerEvents: resendingEmail === item.email ? 'none' : 'auto' }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
                            <polyline points="22 2 15 22 11 12 2 9 22 2"/><line x1="22" y1="2" x2="11" y2="12"/>
                          </svg>
                          {resendingEmail === item.email ? tr("Sending…") : tr("Resend invite")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

        {/* ── Active / inactive list ── */}
        {mode != 3 &&
          teamList.map((item, idx) => {
            const isExternal  = item.member_type === "external";
            const initials    = ((item.first_name || "").charAt(0) + (item.last_name || "").charAt(0)).toUpperCase() || (item.name || "?").charAt(0).toUpperCase();
            const avatarColor = mode == 1 ? `#${item.profile_name_colorcode || "6b7280"}` : "#e5e7eb";
            const rateDisplay = isExternal
              ? formatRate(item.currency, item.billable_rate)
              : null;
            const defRateStr  = wd.default_currency || wd.default_billable_rate != null
              ? `${wd.default_currency || ""} ${wd.default_billable_rate ?? 0}/hr`.trim()
              : "—";

            return (
              <div className="tm-row--data" key={idx}>
                {/* Avatar */}
                <div className="tm-cell tm-cell--avatar">
                  <div
                    className={`tm-avatar${mode != 1 ? " tm-avatar--inactive" : ""}`}
                    style={{ background: avatarColor }}
                  >
                    {initials}
                  </div>
                </div>

                {/* Name */}
                <div className="tm-cell tm-cell--name">
                  <div className="tm-member-name">{item.name}</div>
                </div>

                {/* Role badge */}
                <div className="tm-cell tm-cell--role">
                  <span className={`tm-role-badge tm-role-badge--${(item.workspace_role || 'MEMBER').toLowerCase()}`}>
                    {item.workspace_role === 'ADMIN' ? tr("Admin") : item.workspace_role === 'MANAGER' ? 'Manager' : tr("Member")}
                  </span>
                </div>

                {/* Type badge */}
                <div className="tm-cell tm-cell--type">
                  <span className={`tm-type-badge tm-type-badge--${isExternal ? "external" : "internal"}`}>
                    {isExternal ? tr("External") : tr("Internal")}
                  </span>
                </div>

                {/* Email */}
                <div className="tm-cell tm-cell--email">
                  <span className="tm-email">{item.email}</span>
                </div>

                {/* Billable Rate */}
                <div className="tm-cell tm-cell--rate">
                  {isExternal ? (
                    <span className="tm-rate-val">
                      {rateDisplay ?? <span className="tm-rate-dash">—</span>}
                    </span>
                  ) : (
                    <span className="tm-rate-inherited">{tr("Workspace default (")}{defRateStr})
                    </span>
                  )}
                </div>

                {/* Labor Cost */}
                <div className="tm-cell tm-cell--cost">
                  {isExternal && item.labor_cost > 0 ? (
                    <span className="tm-rate-val">
                      {formatRate(item.currency, item.labor_cost)}
                    </span>
                  ) : (
                    <span className="tm-rate-dash">—</span>
                  )}
                </div>

                {/* Actions */}
                {isAdmin && (
                  <div className="tm-cell tm-cell--actions" ref={clickedId === item.team_id ? dropdownRef : null}>
                    <div className="tm-dropdown">
                      <div
                        className={`tm-dot-btn${clickedId === item.team_id ? " active" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setClickedId(clickedId === item.team_id ? null : item.team_id);
                        }}
                        title={tr("More actions")}
                      >
                        <svg width="4" height="16" viewBox="0 0 4 16">
                          <path
                            fill="currentColor"
                            fillRule="evenodd"
                            d="M0 2a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm0 12a2 2 0 1 1 4 0 2 2 0 0 1-4 0zm0-6a2 2 0 1 1 4 0 2 2 0 0 1-4 0z"
                          />
                        </svg>
                      </div>
                      {clickedId === item.team_id && (
                        <div className="tm-dropdown-menu">
                          {mode == 1 && (
                            <div className="tm-dropdown-item" onClick={() => openEditModal(item)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>{tr("Edit member")} </div>
                          )}
                          {mode == 2 && (
                            <div className="tm-dropdown-item" onClick={() => openConfirm('activate', item)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"/>
                              </svg>{tr("Reactivate")} </div>
                          )}
                          {String(user_id) !== String(item.user_id) && (
                            <div className="tm-dropdown-divider" />
                          )}
                          {mode == 1 && String(user_id) !== String(item.user_id) && (
                            <div className="tm-dropdown-item tm-dropdown-item--warning" onClick={() => openConfirm('deactivate', item)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                              </svg>{tr("Deactivate")} </div>
                          )}
                          {String(user_id) !== String(item.user_id) && (
                            <div className="tm-dropdown-item tm-dropdown-item--danger" onClick={() => openConfirm('remove', item)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                              </svg>{tr("Remove from workspace")} </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        {/* ── Empty state ── */}
        {teamList.length === 0 && (
          <div className="proj-empty">
            <div className="proj-empty-icon-wrap">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div className="proj-empty-title">{tr("No members found")}</div>
            <div className="proj-empty-sub">
              {mode == 1 ? tr("Invite team members to get started.") : tr("No members in this view.")}
            </div>
          </div>
        )}
      </div>

      {/* ── Confirm action modal ── */}
      <Modal
        show={confirmOpen}
        onHide={() => { setConfirmOpen(false); setConfirmAction(null); }}
        dialogClassName="add-New-tag-popup-box-container"
        bsPrefix=""
      >
        <div className="pm-header" style={{ background: confirmAction?.type === 'remove' ? '#b91c1c' : '#92400e' }}>
          <h3 className="pm-title">
            {confirmAction?.type === 'deactivate' && tr("Deactivate member")}
            {confirmAction?.type === 'activate'   && tr("Activate member")}
            {confirmAction?.type === 'remove'     && tr("Remove from workspace")}
          </h3>
          <button className="pm-close" onClick={() => { setConfirmOpen(false); setConfirmAction(null); }}>✕</button>
        </div>
        <div className="pm-body">
          <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
            {confirmAction?.type === 'deactivate' && (
              <>{tr("Are you sure you want to deactivate")} <strong>{confirmAction.item?.name}</strong>{tr("? They will lose workspace access but their data will be retained. You can reactivate them later.")}</>
            )}
            {confirmAction?.type === 'activate' && (
              <>{tr("Reactivate")} <strong>{confirmAction.item?.name}</strong> {tr("and restore their access to this workspace?")}</>
            )}
            {confirmAction?.type === 'remove' && (
              <>{tr("Are you sure you want to permanently remove")} <strong>{confirmAction.item?.name}</strong> {tr("from this workspace? This cannot be undone. Their time entries and tracked data will be retained.")}</>
            )}
          </p>
        </div>
        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={() => { setConfirmOpen(false); setConfirmAction(null); }}>{tr("Cancel")} </button>
          <button
            className="pm-btn-create"
            style={{ background: confirmAction?.type === 'remove' ? '#b91c1c' : confirmAction?.type === 'deactivate' ? '#92400e' : '#1a5c30' }}
            onClick={runConfirm}
            disabled={confirmLoading}
          >
            {confirmLoading ? tr("Please wait…") : (
              confirmAction?.type === 'deactivate' ? tr("Deactivate") :
              confirmAction?.type === 'activate'   ? tr("Activate")   :
              tr("Remove")
            )}
          </button>
        </div>
      </Modal>

      {/* ── Edit Member Modal ── */}
      <Modal
        show={editOpen}
        onHide={handleClose}
        dialogClassName="add-New-tag-popup-box-container"
        bsPrefix=""
      >
        <div className="pm-header" style={{ background: "#1a5c30" }}>
          <h3 className="pm-title">{tr("Edit Member")}</h3>
          <button className="pm-close" onClick={handleClose}>✕</button>
        </div>

        <div className="pm-body">
          {/* Name */}
          <div className="pm-field">
            <label className="pm-label">Name</label>
            <input
              className="pm-input"
              type="text"
              placeholder={tr("Display name")}
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
            />
            {errors.member_name && (
              <span className="pm-error">{errors.member_name}</span>
            )}
          </div>

          {/* Workspace Role */}
          <div className="pm-field">
            <label className="pm-label">{tr("Role")}</label>
            {String(editUserId) === String(user_id) ? (
              <div className="tm-info-box">
                <span className="tm-info-box-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </span>{tr("You cannot change your own role.")} </div>
            ) : (
              <select
                className="pm-input"
                value={editWorkspaceRole}
                onChange={(e) => setEditWorkspaceRole(e.target.value)}
              >
                <option value="MEMBER">{tr("Member")}</option>
                <option value="MANAGER">Manager</option>
                <option value="ADMIN">{tr("Admin")}</option>
              </select>
            )}
          </div>

          {/* Member Type toggle */}
          <div className="pm-field">
            <label className="pm-label">{tr("Member Type")}</label>
            <div className="tm-type-toggle">
              <button
                className={`tm-type-toggle-btn${editMemberType === "internal" ? " tm-type-toggle-btn--active" : ""}`}
                onClick={() => setEditMemberType("internal")}
              >{tr("Internal")} </button>
              <button
                className={`tm-type-toggle-btn${editMemberType === "external" ? " tm-type-toggle-btn--active" : ""}`}
                onClick={() => setEditMemberType("external")}
              >{tr("External")} </button>
            </div>
          </div>

          {/* External fields */}
          {editMemberType === "external" ? (
            <>
              <div className="pm-field">
                <label className="pm-label">{tr("Billable Rate")}{" "}
                  <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="pm-input"
                    type="number"
                    placeholder={tr("e.g. 85")}
                    value={editBillableRate}
                    onChange={(e) => setEditBillableRate(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <select
                    className="pm-input"
                    value={editCurrencyId}
                    onChange={(e) => setEditCurrencyId(e.target.value)}
                    style={{ width: 110 }}
                  >
                    <option value="">{tr("Currency *")}</option>
                    {currencies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.currency}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.billable_rate && (
                  <span className="pm-error">{errors.billable_rate}</span>
                )}
                {errors.currency_id && (
                  <span className="pm-error">{errors.currency_id}</span>
                )}
              </div>

              <div className="pm-field">
                <label className="pm-label">{tr("Labor Cost")}{" "}
                  <span className="pm-label-hint">(optional)</span>
                </label>
                <input
                  className="pm-input"
                  type="number"
                  placeholder={tr("e.g. 60")}
                  value={editLaborCost}
                  onChange={(e) => setEditLaborCost(e.target.value)}
                />
              </div>
            </>
          ) : (
            <div className="tm-info-box">
              <span className="tm-info-box-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </span>{tr("This member's rate is inherited from workspace defaults.")} </div>
          )}

          {/* Email (read-only) */}
          <div className="pm-field">
            <label className="pm-label">{tr("Email")}</label>
            <input
              className="pm-input"
              type="text"
              readOnly
              value={memberEmail}
              style={{ background: "#f9fafb", color: "#9ca3af", cursor: "default" }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="pm-footer">
          <button className="pm-btn-cancel" onClick={handleClose}>{tr("Cancel")} </button>
          <button className="pm-btn-create" onClick={editMember}>{tr("Save")} </button>
        </div>
      </Modal>
    </>
  );
}
